import { useState, useEffect, useCallback } from "react";
import axios from "axios";

export default function useServerPagination({
  resource,
  empresa,
  initialLimit = 15,
  debounceMs = 400,
}) {
  const [page, setPage] = useState(1);
  const [limit, setLimitState] = useState(initialLimit);
  const [search, setSearchState] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [data, setData] = useState([]);
  const [totalResult, setTotalResult] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  // Debounce da busca: espera o usuário parar de digitar e volta p/ a página 1.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, debounceMs);
    return () => clearTimeout(id);
  }, [search, debounceMs]);

  const setSearch = useCallback((value) => setSearchState(value), []);
  const setLimit = useCallback((value) => {
    setLimitState(value);
    setPage(1);
  }, []);
  const goToPage = useCallback((n) => setPage(n), []);
  const nextPage = useCallback(() => setPage((p) => p + 1), []);
  const prevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const refetch = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    if (!empresa) return undefined;

    const source = axios.CancelToken.source();
    setLoading(true);
    setError(null);

    resource
      .fetchPage({
        page,
        limit,
        empresa,
        search: debouncedSearch,
        cancelToken: source.token,
      })
      .then((res) => {
        // Defesa contra totalPages inflado: página vazia além da 1ª volta uma página.
        if (Array.isArray(res.data) && res.data.length === 0 && page > 1) {
          setPage((p) => Math.max(1, p - 1));
          return;
        }
        setData(res.data);
        setTotalResult(res.totalResult);
        setTotalPages(res.totalPages);
        setLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return; // requisição obsoleta: ignora
        setError(err);
        setLoading(false);
      });

    return () => source.cancel("stale request");
  }, [resource, empresa, page, limit, debouncedSearch, reloadTick]);

  return {
    data,
    page,
    limit,
    totalResult,
    totalPages,
    search,
    loading,
    error,
    goToPage,
    nextPage,
    prevPage,
    setSearch,
    setLimit,
    refetch,
  };
}
